import { useState, useEffect, useRef } from 'react';
import tw from 'tailwind-styled-components';
import { FaFilePdf, FaTimes, FaUser, FaPaperPlane, FaSpinner, FaExclamationTriangle, FaDownload, FaExternalLinkAlt } from 'react-icons/fa';

const FnFModal = ({
    isOpen,
    onClose,
    fnfData,
    onSendToTenant,
    isSending,
    isGenerating,
    pdfError
}) => {
    const [pdfLoadError, setPdfLoadError] = useState(false);
    const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
    const objectRef = useRef(null);


    useEffect(() => {
        return () => {
            if (pdfObjectUrl) {
                URL.revokeObjectURL(pdfObjectUrl);
            }
        };
    }, [pdfObjectUrl]);

    if (!isOpen) return null;

    const handleSendToTenant = () => {
        if (onSendToTenant && !isSending && fnfData?.link && !fnfData?.sent) {
            onSendToTenant();
        }
    };


    const renderPDFWithObjectTag = () => {
        if (!fnfData?.link) return null;

        const pdfUrl = pdfObjectUrl || fnfData.link;

        return (
            <PDFPreviewContainer>
                <object
                    ref={objectRef}
                    data={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    onError={() => setPdfLoadError(true)}
                    style={{ backgroundColor: "#fff" }}
                >

                </object>
            </PDFPreviewContainer>
        );
    };

    return (
        <ModalOverlay>
            <ModalInnerContainer>
                <ModalBackdrop onClick={onClose} />

                <ModalContainer>
                    <ModalHeader>
                        <HeaderContent>
                            <HeaderLeft>
                                <PDFIcon size={20} />
                                <HeaderTitle>
                                    Pre Agreement Settlement
                                </HeaderTitle>
                            </HeaderLeft>
                            <HeaderButton onClick={onClose}>
                                <FaTimes size={20} />
                            </HeaderButton>
                        </HeaderContent>
                    </ModalHeader>

                    <ContentArea>
                        {isGenerating ? (
                            <LoadingContainer>
                                <LoadingSpinner size={48} />
                                <LoadingText>Loading document...</LoadingText>
                                <LoadingSubtext>Please wait</LoadingSubtext>
                            </LoadingContainer>
                        ) : pdfError ? (
                            <ErrorContainer>
                                <ErrorIcon size={48} />
                                <ErrorText>Failed to load PDF</ErrorText>
                                <ErrorMessage>{pdfError}</ErrorMessage>
                            </ErrorContainer>
                        ) : fnfData?.link ? (
                            pdfLoadError ? (
                                <PDFFallbackContainer>
                                    <ErrorIcon size={48} />
                                    <PDFFallbackText>Preview Unavailable</PDFFallbackText>
                                    <PDFFallbackSubtext>
                                        Unable to display PDF preview. Please download or open in a new tab.
                                    </PDFFallbackSubtext>
                                    <ButtonGroup>
                                        <BaseButton
                                            href={fnfData.link}
                                            download="settlement-summary.pdf"
                                        >
                                            <FaDownload className="mr-2" size={14} />
                                            Download PDF
                                        </BaseButton>
                                        <BaseButton
                                            href={fnfData.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaExternalLinkAlt className="mr-2" size={14} />
                                            Open in New Tab
                                        </BaseButton>
                                    </ButtonGroup>
                                </PDFFallbackContainer>
                            ) : (
                                renderPDFWithObjectTag()
                            )
                        ) : (
                            <NoDocumentContainer>
                                <NoDocumentIcon size={48} />
                                <NoDocumentText>No document available</NoDocumentText>
                                <NoDocumentSubtext>Generate a settlement summary first</NoDocumentSubtext>
                            </NoDocumentContainer>
                        )}
                    </ContentArea>

                    <ModalFooter>
                        <HeaderContent>
                            <FooterButtonGroup>

                                <div className='hidden md:block'>
                                    {fnfData?.link && (
                                        <BaseButton
                                            href={fnfData.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaDownload className="mr-2" size={14} />
                                            Download PDF
                                        </BaseButton>
                                    )}
                                </div>
                            </FooterButtonGroup>

                            <FooterActions>
                                <CloseButton onClick={onClose}>
                                    Close
                                </CloseButton>

                                {/* <IconButton
                                    onClick={handleSendToTenant}
                                    disabled={isSending || !fnfData?.link || fnfData?.sent}
                                    className={`
                                        ${fnfData?.sent
                                            ? 'bg-green-600 hover:bg-green-700 cursor-default'
                                            : isSending || !fnfData?.link
                                                ? 'opacity-60 cursor-not-allowed bg-primary-color'
                                                : 'bg-primary-color'
                                        }
                                    `}
                                >
                                    {fnfData?.sent ? (
                                        <>
                                            <FaPaperPlane className="mr-2" size={14} />
                                            Sent to Owner
                                        </>
                                    ) : isSending ? (
                                        <>
                                            <FaSpinner className="animate-spin mr-2" size={14} />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane className="mr-2" size={14} />
                                            Send to owner
                                        </>
                                    )}
                                </IconButton> */}
                            </FooterActions>
                        </HeaderContent>
                    </ModalFooter>
                </ModalContainer>
            </ModalInnerContainer>
        </ModalOverlay>
    );
};

// Styled Components
const ModalOverlay = tw.div`fixed inset-0 z-50 `;
const ModalBackdrop = tw.div`fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75`;
const ModalContainer = tw.div`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full h-[90vh] flex flex-col mx-auto`;
const ModalInnerContainer = tw.div`flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0`;
const ModalHeader = tw.div`bg-white px-6 py-4 border-b border-gray-200`;
const HeaderContent = tw.div`flex justify-between items-center`;
const HeaderLeft = tw.div`flex items-center`;
const HeaderTitle = tw.h3`text-lg leading-6 font-medium text-gray-900`;
const HeaderButton = tw.button`text-gray-400 hover:text-gray-500 focus:outline-none transition-colors`;
const SentStatus = tw.div`mt-2 flex items-center text-sm text-green-600 bg-green-50 px-3 py-1 rounded-md`;
const ContentArea = tw.div`flex-1 overflow-hidden bg-gray-100 p-4`;
const LoadingContainer = tw.div`flex flex-col items-center justify-center h-full`;
const LoadingSpinner = tw(FaSpinner)`animate-spin text-primary-color mb-4`;
const LoadingText = tw.p`text-gray-600 text-lg`;
const LoadingSubtext = tw.p`text-gray-400 text-sm mt-2`;
const ErrorContainer = tw.div`flex flex-col items-center justify-center h-full`;
const ErrorIcon = tw(FaExclamationTriangle)`text-red-500 mb-4`;
const ErrorText = tw.p`text-red-600 text-lg`;
const ErrorMessage = tw.p`text-gray-500 text-sm mt-2`;
const PDFPreviewContainer = tw.div`h-full border border-gray-300 rounded-lg overflow-hidden bg-white relative`;
const PDFFallbackContainer = tw.div`flex flex-col items-center justify-center h-full p-8 bg-white rounded-lg border border-gray-300`;
const PDFFallbackText = tw.p`text-red-600 text-lg font-medium mb-2`;
const PDFFallbackSubtext = tw.p`text-gray-500 text-sm mb-6 text-center`;
const NoDocumentContainer = tw.div`flex flex-col items-center justify-center h-full`;
const NoDocumentIcon = tw(FaFilePdf)`text-gray-400 mb-4`;
const NoDocumentText = tw.p`text-gray-500 text-lg`;
const NoDocumentSubtext = tw.p`text-gray-400 text-sm mt-2`;
const ButtonGroup = tw.div`flex space-x-4`;
const BaseButton = tw.a`inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors cursor-pointer`;
const IconButton = tw.button`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md border border-transparent transition-colors`;
const CloseButton = tw.button`inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors`;
const ModalFooter = tw.div`bg-gray-50 px-6 py-4 border-t border-gray-200`;
const FooterButtonGroup = tw.div`flex items-center space-x-3`;
const FooterActions = tw.div`flex space-x-3`;
const PDFIcon = tw(FaFilePdf)`text-red-500 mr-2`;

export default FnFModal;